import { Button, Card, Typography, Empty, Spin, Radio, Collapse, DatePicker } from "antd";
import dayjs from "dayjs";
import { Calendar, ArrowLeft, ArrowRight, Truck, CalendarDays, List } from "lucide-react";
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetMyServiceTickets } from "~/services/service-ticket";
import { JobStatus } from "~/utils/constants";
import { AssignmentTicket } from "./assignment-ticket";
import { CompleteServiceTicketModal } from "~/components/common/complete-ticket";
import { AssignmentsCalendarView } from "./assignments-calendar-view";
import type { Dayjs } from "dayjs";

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface AssignmentsTabProps {}

interface GroupedAssignments {
  [key: string]: {
    dateLabel: string;
    tickets: any[];
  };
}

interface VesselGroup {
  vessel: any;
  tickets: any[];
}

// Utility function to get correct datetime by combining serviceDatetime date with startDatetime time
const getCorrectDateTime = (ticket: any) => {
  if (!ticket.serviceDatetime || !ticket.startDatetime) {
    return ticket.serviceDatetime || ticket.startDatetime;
  }

  const serviceDate = dayjs(ticket.serviceDatetime);
  const startTime = dayjs(ticket.startDatetime);

  // Combine: date from serviceDatetime + time from startDatetime
  return serviceDate
    .hour(startTime.hour())
    .minute(startTime.minute())
    .second(startTime.second())
    .millisecond(startTime.millisecond())
    .toDate();
};

export const Assignments: React.FC<AssignmentsTabProps> = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const expandedServiceTicketRef = useRef<boolean>(false);

  // Parse URL params with memoized functions
  const urlParams = useMemo(() => {
    const viewModeParam = searchParams.get("view");
    const dateParam = searchParams.get("date");
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");

    const viewMode = viewModeParam === "week" ? "week" : "day";
    const currentDate = (() => {
      if (startDateParam && dayjs(startDateParam).isValid()) {
        return dayjs(startDateParam);
      }
      if (dateParam && dayjs(dateParam).isValid()) {
        return dayjs(dateParam);
      }
      return dayjs();
    })();
    const hasCustomRange = !!(
      startDateParam &&
      endDateParam &&
      dayjs(startDateParam).isValid() &&
      dayjs(endDateParam).isValid()
    );

    return {
      viewMode,
      currentDate,
      hasCustomRange,
      startDate: startDateParam,
      endDate: endDateParam,
    };
  }, [searchParams]);

  // Local state (only for UI, not for queries)
  const [viewMode, setViewMode] = useState<"day" | "week">(urlParams.viewMode);
  const [currentDate, setCurrentDate] = useState(urlParams.currentDate);
  const [displayMode, setDisplayMode] = useState<"list" | "calendar">("list");
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Calculate date range for queries - memoized and stable
  const queryParams = useMemo(() => {
    let startDate: string;
    let endDate: string;

    if (urlParams.hasCustomRange) {
      startDate = urlParams.startDate!;
      endDate = urlParams.endDate!;
    } else {
      const date = urlParams.currentDate;
      if (urlParams.viewMode === "day") {
        startDate = date.startOf("day").toISOString();
        endDate = date.endOf("day").toISOString();
      } else {
        startDate = date.startOf("week").toISOString();
        endDate = date.endOf("week").toISOString();
      }
    }

    return {
      startDate,
      endDate,
      // Remove status filter - get all assignments
    };
  }, [urlParams]);

  // Single query call with stable params - get all assignments
  const { isLoading, data: serviceTickets } =
    useGetMyServiceTickets(queryParams);

  // Update URL helper - removed tab parameter
  const updateURL = useCallback(
    (updates: {
      view?: "day" | "week";
      date?: dayjs.Dayjs;
      startDate?: string;
      endDate?: string;
      clearCustomRange?: boolean;
    }) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (updates.clearCustomRange) {
          newParams.delete("startDate");
          newParams.delete("endDate");
        }

        if (updates.startDate && updates.endDate) {
          newParams.set("startDate", updates.startDate);
          newParams.set("endDate", updates.endDate);
          newParams.delete("date");
          newParams.delete("view");
        } else {
          if (!newParams.has("startDate") && !newParams.has("endDate")) {
            if (updates.view !== undefined) {
              if (updates.view === "day") {
                newParams.delete("view");
              } else {
                newParams.set("view", updates.view);
              }
            }

            if (updates.date !== undefined) {
              const today = dayjs().format("YYYY-MM-DD");
              const dateStr = updates.date.format("YYYY-MM-DD");
              if (dateStr === today) {
                newParams.delete("date");
              } else {
                newParams.set("date", dateStr);
              }
            }
          }
        }


        return newParams;
      });
    },
    [setSearchParams]
  );

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    const newDate =
      viewMode === "day"
        ? currentDate.subtract(1, "day")
        : currentDate.subtract(1, "week");

    setCurrentDate(newDate);
    updateURL({ date: newDate, clearCustomRange: true });
  }, [currentDate, viewMode, updateURL]);

  const goToNext = useCallback(() => {
    const newDate =
      viewMode === "day"
        ? currentDate.add(1, "day")
        : currentDate.add(1, "week");

    setCurrentDate(newDate);
    updateURL({ date: newDate, clearCustomRange: true });
  }, [currentDate, viewMode, updateURL]);

  const goToToday = useCallback(() => {
    const today = dayjs();
    setCurrentDate(today);
    updateURL({ date: today, clearCustomRange: true });
  }, [updateURL]);

  const handleViewModeChange = useCallback(
    (mode: "day" | "week") => {
      setViewMode(mode);
      updateURL({ view: mode, clearCustomRange: true });
    },
    [updateURL]
  );

  const handleDatePickerChange = useCallback(
    (date: Dayjs | null) => {
      if (date) {
        setCurrentDate(date);
        updateURL({ date, clearCustomRange: true });
        setDatePickerOpen(false);
      }
    },
    [updateURL]
  );

  // Group tickets by status
  const ticketsByStatus = useMemo(() => {
    if (!serviceTickets?.items) return { open: [], inProgress: [], completed: [] };

    return {
      open: serviceTickets.items.filter((ticket) => ticket.status === JobStatus.SCHEDULED),
      inProgress: serviceTickets.items.filter((ticket) => ticket.status === JobStatus.IN_PROGRESS),
      completed: serviceTickets.items.filter((ticket) => ticket.status === JobStatus.COMPLETED),
    };
  }, [serviceTickets?.items]);

  // Get the target service ticket ID from URL for highlighting
  const targetServiceTicketId = searchParams.get('serviceTicketId');

  // Handle auto-expand from notification navigation
  useEffect(() => {
    const serviceTicketId = searchParams.get('serviceTicketId');

    if (!serviceTicketId || !serviceTickets?.items || expandedServiceTicketRef.current) {
      return;
    }

    // Find the target ticket directly from serviceTickets
    const targetTicket = serviceTickets.items.find(t => t.id === serviceTicketId);

    if (targetTicket) {
      // Determine which status panel to expand based on ticket status
      const statusKey = targetTicket.status === JobStatus.SCHEDULED ? 'open' :
                       targetTicket.status === JobStatus.IN_PROGRESS ? 'in-progress' : 'completed';

      setActiveKeys([statusKey]);
      expandedServiceTicketRef.current = true;

      // Scroll to the ticket after a brief delay to allow collapse animation
      setTimeout(() => {
        const element = document.getElementById(`ticket-${serviceTicketId}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [serviceTickets?.items, searchParams]);

  // Helper function to organize tickets by date and vessel
  const organizeTicketsByDateAndVessel = useCallback((tickets: any[]) => {
    if (tickets.length === 0) {
      return {};
    }

    // First, group by date
    const byDate = tickets.reduce((acc: any, ticket) => {
      const dateKey = dayjs(getCorrectDateTime(ticket)).format("YYYY-MM-DD");
      const dateLabel = dayjs(getCorrectDateTime(ticket)).format("dddd, MMMM D");

      if (!acc[dateKey]) {
        acc[dateKey] = {
          dateLabel,
          vessels: {},
          ticketsWithoutVessel: []
        };
      }

      // Group by vessel within each date
      if (ticket.vessel) {
        const vesselId = ticket.vessel.id;
        if (!acc[dateKey].vessels[vesselId]) {
          acc[dateKey].vessels[vesselId] = {
            vessel: ticket.vessel,
            tickets: []
          };
        }
        acc[dateKey].vessels[vesselId].tickets.push(ticket);
      } else {
        acc[dateKey].ticketsWithoutVessel.push(ticket);
      }

      return acc;
    }, {});

    // Sort tickets within each vessel by start time
    Object.keys(byDate).forEach(dateKey => {
      Object.keys(byDate[dateKey].vessels).forEach(vesselId => {
        byDate[dateKey].vessels[vesselId].tickets.sort((a: any, b: any) =>
          dayjs(getCorrectDateTime(a)).valueOf() - dayjs(getCorrectDateTime(b)).valueOf()
        );
      });
      
      byDate[dateKey].ticketsWithoutVessel.sort((a: any, b: any) =>
        dayjs(getCorrectDateTime(a)).valueOf() - dayjs(getCorrectDateTime(b)).valueOf()
      );
    });

    return byDate;
  }, []);

  // Count assignments by status for display
  const statusCounts = useMemo(() => {
    if (!serviceTickets?.items) return { total: 0, inProgress: 0, scheduled: 0, completed: 0 };
    
    const counts = serviceTickets.items.reduce((acc, ticket) => {
      acc.total++;
      if (ticket.status === JobStatus.IN_PROGRESS) acc.inProgress++;
      else if (ticket.status === JobStatus.SCHEDULED) acc.scheduled++;
      else if (ticket.status === JobStatus.COMPLETED) acc.completed++;
      return acc;
    }, { total: 0, inProgress: 0, scheduled: 0, completed: 0 });
    
    return counts;
  }, [serviceTickets?.items]);

  // Memoized date navigation component
  const dateNavigation = useMemo(() => {
    if (urlParams.hasCustomRange) {
      const startDateObj = dayjs(urlParams.startDate);
      const endDateObj = dayjs(urlParams.endDate);

      return (
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="flex items-center space-x-2 md:space-x-3 flex-wrap">
            <Button type="primary" onClick={goToToday} size="small">
              Back to Today
            </Button>
            <Button
              type="default"
              onClick={() => updateURL({ clearCustomRange: true })}
              size="small"
            >
              Clear Custom Range
            </Button>
          </div>

          <div className="flex-1 text-center md:text-left">
            <Text strong className="text-sm md:text-base">
              Custom Range: {startDateObj.format("MMM D")} -{" "}
              {endDateObj.format("MMM D, YYYY")}
            </Text>
          </div>

          <div className="text-xs text-gray-500">Custom date range active</div>
        </div>
      );
    }

    return (
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="flex items-center space-x-2 md:space-x-3 flex-wrap">
          <Button
            type="text"
            icon={<ArrowLeft size={16} className="md:size-5" />}
            onClick={goToPrevious}
            size="small"
          />
          <Button type="primary" onClick={goToToday} size="small">
            Today
          </Button>
          <Button
            type="text"
            icon={<ArrowRight size={16} className="md:size-5" />}
            onClick={goToNext}
            size="small"
          />
          <DatePicker
            value={currentDate}
            onChange={handleDatePickerChange}
            open={datePickerOpen}
            onOpenChange={setDatePickerOpen}
            format="MMM D, YYYY"
            size="small"
            placeholder="Select date"
            allowClear={false}
            className="w-auto"
            suffixIcon={<Calendar size={14} />}
          />
        </div>

        <div className="flex-1 text-center md:text-left">
          <Text strong className="text-sm md:text-base">
            {viewMode === "day"
              ? currentDate.format("MMMM D, YYYY")
              : `${currentDate.startOf("week").format("MMM D")} - ${currentDate
                  .endOf("week")
                  .format("MMM D, YYYY")}`}
          </Text>
        </div>

        <div className="flex space-x-2 md:space-x-3">
          <Button
            type={viewMode === "day" ? "primary" : "default"}
            onClick={() => handleViewModeChange("day")}
            size="small"
          >
            Day
          </Button>
          <Button
            type={viewMode === "week" ? "primary" : "default"}
            onClick={() => handleViewModeChange("week")}
            size="small"
          >
            Week
          </Button>
        </div>
      </div>
    );
  }, [
    urlParams.hasCustomRange,
    urlParams.startDate,
    urlParams.endDate,
    currentDate,
    viewMode,
    goToPrevious,
    goToNext,
    goToToday,
    handleViewModeChange,
    updateURL,
    handleDatePickerChange,
    datePickerOpen,
  ]);

  if (isLoading) {
    return (
      <div className="space-y-3 md:space-y-4">
        <Title level={3} className="!mb-0 text-lg md:text-xl">
          My Assignments
        </Title>
        <Card className="border-0 md:border">
          <div className="text-center py-6 md:py-8">
            <Spin size="large" />
            <Text
              type="secondary"
              className="block mt-3 md:mt-4 text-sm md:text-base"
            >
              Loading my assignments...
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4 mb-3 md:mb-4">
        <div className="flex items-center space-x-3">
          <Title level={3} className="!mb-0 text-lg md:text-xl">
            My Assignments
          </Title>
          {statusCounts.total > 0 && (
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <span>({statusCounts.total} total)</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            type={displayMode === "list" ? "primary" : "default"}
            icon={<List size={16} />}
            onClick={() => setDisplayMode("list")}
            size="small"
          >
            List
          </Button>
          <Button
            type={displayMode === "calendar" ? "primary" : "default"}
            icon={<CalendarDays size={16} />}
            onClick={() => setDisplayMode("calendar")}
            size="small"
          >
            Calendar
          </Button>
        </div>
      </div>

      {dateNavigation}

      {/* Calendar View */}
      {displayMode === "calendar" && (
        <div className="mb-4">
          <AssignmentsCalendarView
            tickets={serviceTickets?.items || []}
            targetServiceTicketId={targetServiceTicketId}
            startDate={
              urlParams.hasCustomRange
                ? dayjs(urlParams.startDate!)
                : urlParams.currentDate.startOf(viewMode === "day" ? "day" : "isoWeek")
            }
            endDate={
              urlParams.hasCustomRange
                ? dayjs(urlParams.endDate!)
                : urlParams.currentDate.endOf(viewMode === "day" ? "day" : "isoWeek")
            }
            viewMode={urlParams.hasCustomRange ? "week" : viewMode}
          />
        </div>
      )}

      {/* List View - Collapsible Status Sections */}
      {displayMode === "list" && (
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys as string[])}
        className="bg-white"
        expandIconPosition="end"
      >
        {/* Open Section */}
        <Panel
          header={
            <span className="font-medium text-base">
              Open {statusCounts.scheduled > 0 && `(${statusCounts.scheduled})`}
            </span>
          }
          key="open"
        >
          {ticketsByStatus.open.length > 0 ? (
            <div className="space-y-4 md:space-y-6">
              {Object.entries(organizeTicketsByDateAndVessel(ticketsByStatus.open))
            .sort(([dateKeyA], [dateKeyB]) => dateKeyA.localeCompare(dateKeyB))
            .map(([dateKey, dateGroup]: [string, any]) => (
              <div key={dateKey} className="space-y-3 md:space-y-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 md:gap-3">
                    <Calendar size={16} className="text-gray-500 flex-shrink-0" />
                    <Text strong className="text-lg">{dateGroup.dateLabel}</Text>
                  </div>
                </div>

                {/* Vessel-grouped assignments */}
                {Object.entries(dateGroup.vessels).map(([vesselId, vesselGroup]: [string, any]) => (
                  <div key={vesselId} className="mb-4">
                    <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                      <Truck size={14} className="text-blue-600" />
                      <Text strong className="text-sm">
                        {vesselGroup.vessel.name || vesselGroup.vessel.vesselRef}
                      </Text>
                      <Text type="secondary" className="text-xs">
                        ({vesselGroup.tickets.length} {vesselGroup.tickets.length === 1 ? 'service' : 'services'})
                      </Text>
                    </div>
                    <div className="space-y-2 md:space-y-3 ml-4">
                      {vesselGroup.tickets.map((assignment: any) => (
                        <div key={assignment.id} id={`ticket-${assignment.id}`}>
                          <AssignmentTicket
                            ticket={assignment}
                            variant="compact"
                            initialExpanded={assignment.id === targetServiceTicketId}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Non-vessel assignments */}
                {dateGroup.ticketsWithoutVessel.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                      <Calendar size={14} className="text-gray-600" />
                      <Text strong className="text-sm">
                        Other Assignments
                      </Text>
                      <Text type="secondary" className="text-xs">
                        ({dateGroup.ticketsWithoutVessel.length} {dateGroup.ticketsWithoutVessel.length === 1 ? 'service' : 'services'})
                      </Text>
                    </div>
                    <div className="space-y-2 md:space-y-3 ml-4">
                      {dateGroup.ticketsWithoutVessel.map((assignment: any) => (
                        <div key={assignment.id} id={`ticket-${assignment.id}`}>
                          <AssignmentTicket
                            ticket={assignment}
                            variant="compact"
                            initialExpanded={assignment.id === targetServiceTicketId}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          ) : (
            <Empty description="No open assignments" />
          )}
        </Panel>

        {/* In Progress Section */}
        <Panel
          header={
            <span className="font-medium text-base">
              In Progress {statusCounts.inProgress > 0 && `(${statusCounts.inProgress})`}
            </span>
          }
          key="in-progress"
        >
          {ticketsByStatus.inProgress.length > 0 ? (
            <div className="space-y-4 md:space-y-6">
              {Object.entries(organizeTicketsByDateAndVessel(ticketsByStatus.inProgress))
            .sort(([dateKeyA], [dateKeyB]) => dateKeyA.localeCompare(dateKeyB))
            .map(([dateKey, dateGroup]: [string, any]) => (
              <div key={dateKey} className="space-y-3 md:space-y-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 md:gap-3">
                    <Calendar size={16} className="text-gray-500 flex-shrink-0" />
                    <Text strong className="text-lg">{dateGroup.dateLabel}</Text>
                  </div>
                </div>

                {/* Vessel-grouped assignments */}
                {Object.entries(dateGroup.vessels).map(([vesselId, vesselGroup]: [string, any]) => (
                  <div key={vesselId} className="mb-4">
                    <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                      <Truck size={14} className="text-blue-600" />
                      <Text strong className="text-sm">
                        {vesselGroup.vessel.name || vesselGroup.vessel.vesselRef}
                      </Text>
                      <Text type="secondary" className="text-xs">
                        ({vesselGroup.tickets.length} {vesselGroup.tickets.length === 1 ? 'service' : 'services'})
                      </Text>
                    </div>
                    <div className="space-y-2 md:space-y-3 ml-4">
                      {vesselGroup.tickets.map((assignment: any) => (
                        <div key={assignment.id} id={`ticket-${assignment.id}`}>
                          <AssignmentTicket
                            ticket={assignment}
                            variant="compact"
                            initialExpanded={assignment.id === targetServiceTicketId}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Non-vessel assignments */}
                {dateGroup.ticketsWithoutVessel.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                      <Calendar size={14} className="text-gray-600" />
                      <Text strong className="text-sm">
                        Other Assignments
                      </Text>
                      <Text type="secondary" className="text-xs">
                        ({dateGroup.ticketsWithoutVessel.length} {dateGroup.ticketsWithoutVessel.length === 1 ? 'service' : 'services'})
                      </Text>
                    </div>
                    <div className="space-y-2 md:space-y-3 ml-4">
                      {dateGroup.ticketsWithoutVessel.map((assignment: any) => (
                        <div key={assignment.id} id={`ticket-${assignment.id}`}>
                          <AssignmentTicket
                            ticket={assignment}
                            variant="compact"
                            initialExpanded={assignment.id === targetServiceTicketId}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          ) : (
            <Empty description="No in progress assignments" />
          )}
        </Panel>

        {/* Completed Section */}
        <Panel
          header={
            <span className="font-medium text-base">
              Completed {statusCounts.completed > 0 && `(${statusCounts.completed})`}
            </span>
          }
          key="completed"
        >
          {ticketsByStatus.completed.length > 0 ? (
            <div className="space-y-4 md:space-y-6">
              {Object.entries(organizeTicketsByDateAndVessel(ticketsByStatus.completed))
            .sort(([dateKeyA], [dateKeyB]) => dateKeyA.localeCompare(dateKeyB))
            .map(([dateKey, dateGroup]: [string, any]) => (
              <div key={dateKey} className="space-y-3 md:space-y-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 md:gap-3">
                    <Calendar size={16} className="text-gray-500 flex-shrink-0" />
                    <Text strong className="text-lg">{dateGroup.dateLabel}</Text>
                  </div>
                </div>

                {/* Vessel-grouped assignments */}
                {Object.entries(dateGroup.vessels).map(([vesselId, vesselGroup]: [string, any]) => (
                  <div key={vesselId} className="mb-4">
                    <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                      <Truck size={14} className="text-blue-600" />
                      <Text strong className="text-sm">
                        {vesselGroup.vessel.name || vesselGroup.vessel.vesselRef}
                      </Text>
                      <Text type="secondary" className="text-xs">
                        ({vesselGroup.tickets.length} {vesselGroup.tickets.length === 1 ? 'service' : 'services'})
                      </Text>
                    </div>
                    <div className="space-y-2 md:space-y-3 ml-4">
                      {vesselGroup.tickets.map((assignment: any) => (
                        <div key={assignment.id} id={`ticket-${assignment.id}`}>
                          <AssignmentTicket
                            ticket={assignment}
                            variant="compact"
                            initialExpanded={assignment.id === targetServiceTicketId}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Non-vessel assignments */}
                {dateGroup.ticketsWithoutVessel.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                      <Calendar size={14} className="text-gray-600" />
                      <Text strong className="text-sm">
                        Other Assignments
                      </Text>
                      <Text type="secondary" className="text-xs">
                        ({dateGroup.ticketsWithoutVessel.length} {dateGroup.ticketsWithoutVessel.length === 1 ? 'service' : 'services'})
                      </Text>
                    </div>
                    <div className="space-y-2 md:space-y-3 ml-4">
                      {dateGroup.ticketsWithoutVessel.map((assignment: any) => (
                        <div key={assignment.id} id={`ticket-${assignment.id}`}>
                          <AssignmentTicket
                            ticket={assignment}
                            variant="compact"
                            initialExpanded={assignment.id === targetServiceTicketId}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          ) : (
            <Empty description="No completed assignments" />
          )}
        </Panel>
      </Collapse>
      )}

      {/* Complete Ticket Modal */}
      <CompleteServiceTicketModal />
    </div>
  );
};